import createClient, { type Middleware } from 'openapi-fetch';

import { ApiError, ApiProtocolError } from './errors';
import type { paths } from './generated/schema';

export interface ContractResponse<TData, TError = unknown> {
  readonly data?: TData;
  readonly error?: TError;
  readonly response: Response;
}

export interface UnauthorizedRequestContext {
  readonly epoch: number;
}

export interface UnauthorizedTransport {
  readonly capture: () => UnauthorizedRequestContext;
  readonly notify: (context: UnauthorizedRequestContext) => void;
}

export interface ContractClientDependencies {
  readonly baseUrl: string;
  readonly fetcher?: (request: Request) => Promise<Response>;
  readonly csrfToken?: () => string | null;
  readonly unauthorized?: UnauthorizedTransport;
}

export function createContractClient(dependencies: ContractClientDependencies) {
  const readCsrfToken = dependencies.csrfToken ?? (() => null);
  const fetcher = dependencies.fetcher ?? ((request: Request) => fetch(request));
  const unauthorized = dependencies.unauthorized;
  const trackedFetcher = async (request: Request): Promise<Response> => {
    const unauthorizedContext = unauthorized?.capture();
    const response = await fetcher(request);
    if (response.status === 401 && unauthorized && unauthorizedContext) {
      unauthorized.notify(unauthorizedContext);
    }
    return response;
  };

  const client = createClient<paths>({
    baseUrl: dependencies.baseUrl,
    credentials: 'include',
    fetch: trackedFetcher,
  });

  const securityMiddleware: Middleware = {
    onRequest({ request }) {
      if (!request.headers.has('Accept')) {
        request.headers.set('Accept', 'application/json');
      }
      if (isUnsafeMethod(request.method)) {
        const token = readCsrfToken();
        if (token && !request.headers.has('X-CSRF-Token')) {
          request.headers.set('X-CSRF-Token', token);
        }
      }
      return request;
    },
  };

  client.use(securityMiddleware);
  return client;
}

export async function executeContractRequest<TData, TError = unknown>(
  request: PromiseLike<ContractResponse<TData, TError>>,
): Promise<TData> {
  const result = await awaitContractResponse(request);
  assertContractSuccess(result);
  if (result.response.status === 204 || result.response.status === 205) {
    return undefined as TData;
  }
  if (result.data === undefined) {
    throw new ApiProtocolError(
      `API returned no payload for successful HTTP ${result.response.status}`,
    );
  }
  return result.data;
}

export async function executeContractStatus<TError = unknown>(
  request: PromiseLike<ContractResponse<unknown, TError>>,
): Promise<Response> {
  const result = await awaitContractResponse(request);
  assertContractSuccess(result);
  return result.response;
}

async function awaitContractResponse<TData, TError>(
  request: PromiseLike<ContractResponse<TData, TError>>,
): Promise<ContractResponse<TData, TError>> {
  try {
    return await request;
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new ApiProtocolError('API returned invalid JSON for a successful response', {
        cause: error,
      });
    }
    throw error;
  }
}

function assertContractSuccess(result: ContractResponse<unknown, unknown>): void {
  if (!result.response.ok) {
    throw apiErrorFromResponse(result.response, result.error);
  }
}

function isUnsafeMethod(method: string): boolean {
  return !['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase());
}

function apiErrorFromResponse(response: Response, payload: unknown): ApiError {
  const record = isRecord(payload) ? payload : null;
  const detail = typeof record?.detail === 'string' ? record.detail : null;
  const message = typeof record?.message === 'string' ? record.message : null;
  const code = typeof record?.code === 'string' ? record.code : null;
  const text = typeof payload === 'string' && payload.trim() ? payload.trim() : null;
  const normalizedDetail = detail ?? message ?? text ?? `HTTP ${response.status}`;

  return new ApiError(normalizedDetail, {
    status: response.status,
    code,
    payload,
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
